import React, { useEffect, useState } from "react";
import { Paper, Typography, Button } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { Refresh } from "@mui/icons-material";
import { DataTable } from "../../common/dataTable/DataTable";
import { ProviderWorkflowMonitoringFilters } from "../providerWorkflowMonitoringFilters/ProviderWorkflowMonitoringFilters";
import useGetProviderWorkflowMonitoring from "../../../hooks/media/useGetProviderWorkflowMonitoring";
import { TABLE_ROWS_PER_PAGE } from "../../../constants";
import { initLocalization } from "../../../utils/helpers";
import { providerWorkflowMonitoringStrings } from "./locale";

const INITIAL_FILTERS = {
  rowKey: "",
  creationDate: "",
  lastModificationDate: "",
  sourceFile: "",
  status: "",
};

const FRONTEND_TO_BACKEND_SORT_MAP = {
  PartitionKey: "PartitionKey",
  RowKey: "RowKey",
  CreationDate: "CreationDate",
  LastModificationDate: "LastModificationDate",
  SourceFile: "SourceFile",
  Status: "Status",
};
const BACKEND_TO_FRONTEND_SORT_MAP = FRONTEND_TO_BACKEND_SORT_MAP;

const DEFAULT_SORTING = { sortOption: { sortColumn: "", direction: "asc" } };

export const ProviderWorkflowMonitoring = () => {
  initLocalization(providerWorkflowMonitoringStrings);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_ROWS_PER_PAGE[0]);
  const [sorting, setSorting] = useState(DEFAULT_SORTING);

  const { items, total, isLoading, getProviderWorkflowMonitoring } =
    useGetProviderWorkflowMonitoring(false);

  const columns = [
    {
      field: "PartitionKey",
      headerName: providerWorkflowMonitoringStrings.partitionKey,
    },
    { field: "RowKey", headerName: providerWorkflowMonitoringStrings.rowKey },
    {
      field: "CreationDate",
      headerName: providerWorkflowMonitoringStrings.creationDate,
    },
    {
      field: "LastModificationDate",
      headerName: providerWorkflowMonitoringStrings.lastModificationDate,
    },
    {
      field: "SourceFile",
      headerName: providerWorkflowMonitoringStrings.sourceFile,
    },
    { field: "Status", headerName: providerWorkflowMonitoringStrings.status },
  ];

  const sortableColumns = columns.map((c) => c.field);

  const runSearch = async (
    p = page,
    size = rowsPerPage,
    showLoader = true,
    overrideSort = sorting.sortOption,
  ) => {
    return getProviderWorkflowMonitoring(
      { ...filters },
      p + 1,
      size,
      showLoader,
      { sortOption: overrideSort },
    );
  };

  const handleSearch = async () => {
    setPage(0);
    await runSearch(0, rowsPerPage, true);
  };

  const handleResetFilters = async () => {
    const defaultSize = TABLE_ROWS_PER_PAGE[0];
    setFilters(INITIAL_FILTERS);
    setPage(0);
    setRowsPerPage(defaultSize);
    setSorting(DEFAULT_SORTING);
    await getProviderWorkflowMonitoring(
      { ...INITIAL_FILTERS },
      1,
      defaultSize,
      true,
      { sortOption: DEFAULT_SORTING.sortOption },
    );
  };

  const handleChangePage = async (_event, newPage) => {
    setPage(newPage);
    await runSearch(newPage, rowsPerPage, false);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    await runSearch(0, newSize, true);
  };

  const handleSort = async (columnId, directionFromDataTable) => {
    const current = sorting.sortOption;
    const isNewColumn = !current.sortColumn || current.sortColumn !== columnId;

    const direction = isNewColumn ? "asc" : directionFromDataTable;

    const sortColumn = FRONTEND_TO_BACKEND_SORT_MAP[columnId] || columnId;
    const nextSort = { sortColumn, direction };

    setSorting({ sortOption: nextSort });
    setPage(0);
    await runSearch(0, rowsPerPage, true, nextSort);
  };

  useEffect(() => {
    runSearch(0, rowsPerPage, true);
  }, []);

  return (
    <Paper
      sx={{
        p: 2,
        height: "100%",
        position: "relative",
        "& .MuiTableSortLabel-icon": { opacity: 1 },
      }}
    >
      <Grid container spacing={2} sx={{ height: "100%" }}>
        <Grid
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          minHeight="32px"
        >
          <Typography variant="h6">
            {providerWorkflowMonitoringStrings.title}
          </Typography>
          <Button
            onClick={handleResetFilters}
            startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
            variant="text"
          >
            {providerWorkflowMonitoringStrings.resetFilters}
          </Button>
        </Grid>

        <Grid xs={12}>
          <ProviderWorkflowMonitoringFilters
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
          />
        </Grid>

        <Grid
          xs={12}
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          <DataTable
            columns={columns}
            rows={items}
            loading={isLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={total}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onSelectRow={() => {}}
            sortableColumns={sortableColumns}
            sortBy={
              BACKEND_TO_FRONTEND_SORT_MAP[sorting.sortOption.sortColumn] ??
              sorting.sortOption.sortColumn
            }
            sortDirection={sorting.sortOption.direction}
            onSort={handleSort}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
